import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation } from '@tanstack/react-query';
import { authApi } from '@/services/api';
import { Logo } from '@/components/Logo';
import { LoadingSpinner } from '@/components/ui';

// Form validation schema
const forgotPasswordSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
});

type ForgotPasswordForm = z.infer<typeof forgotPasswordSchema>;

type PageState = 'form' | 'success' | 'google-only';

export function ForgotPassword() {
  const [pageState, setPageState] = useState<PageState>('form');
  const [submittedEmail, setSubmittedEmail] = useState<string>('');

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ForgotPasswordForm>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: {
      email: '',
    },
  });

  const forgotPasswordMutation = useMutation({
    mutationFn: (email: string) => authApi.forgotPassword(email),
    onSuccess: (response) => {
      if (response.google_only_account) {
        setPageState('google-only');
      } else {
        setPageState('success');
      }
    },
    onError: () => {
      // Even on error, show success for security (prevent email enumeration)
      setPageState('success');
    },
  });

  const onSubmit = async (data: ForgotPasswordForm) => {
    setSubmittedEmail(data.email);
    forgotPasswordMutation.mutate(data.email);
  };

  // Success state - email sent
  if (pageState === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="w-full space-y-8" style={{ maxWidth: '384px', margin: '0 auto' }}>
          <div className="text-center">
            <div className="flex justify-center mb-4">
              <Logo variant="color" size="lg" className="!h-[64px] ml-6" />
            </div>
          </div>

          <div className="bg-white shadow-lg rounded-lg px-8 py-10 text-center">
            <div className="mb-6">
              <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
            </div>

            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              Check your email
            </h2>

            <p className="text-gray-600 mb-6">
              If an account exists for <strong>{submittedEmail}</strong>, you will receive a password reset link shortly.
            </p>

            <p className="text-sm text-gray-500 mb-6">
              The link will expire in 1 hour. If you don't see the email, check your spam folder.
            </p>

            <Link
              to="/login"
              className="inline-block w-full py-2 px-4 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
            >
              Back to sign in
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Google-only account state
  if (pageState === 'google-only') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="w-full space-y-8" style={{ maxWidth: '384px', margin: '0 auto' }}>
          <div className="text-center">
            <div className="flex justify-center mb-4">
              <Logo variant="color" size="lg" className="!h-[64px] ml-6" />
            </div>
          </div>

          <div className="bg-white shadow-lg rounded-lg px-8 py-10 text-center">
            <div className="mb-6">
              <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-blue-600" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
              </div>
            </div>

            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              Sign in with Google
            </h2>

            <p className="text-gray-600 mb-6">
              This account uses Google Sign-In. Please sign in with Google instead of using a password.
            </p>

            <Link
              to="/login"
              className="inline-block w-full py-2 px-4 border border-transparent rounded-md text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
              style={{
                background: 'linear-gradient(90deg, #ff3160 0%, #cc1a40 50%, #ff3160 100%)',
              }}
            >
              Go to sign in
            </Link>

            <button
              type="button"
              onClick={() => setPageState('form')}
              className="mt-4 text-sm text-gray-600 hover:text-gray-800"
            >
              Try a different email
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Form state
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="w-full space-y-8" style={{ maxWidth: '384px', margin: '0 auto' }}>
        <div className="text-center">
          <div className="flex justify-center mb-4">
            <Logo variant="color" size="lg" className="!h-[64px] ml-6" />
          </div>
          <h2 className="text-2xl font-semibold text-gray-900">
            Forgot your password?
          </h2>
          <p className="mt-2 text-gray-600">
            Enter your email and we'll send you a link to reset your password.
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="mt-8 space-y-4 bg-white shadow-lg rounded-lg px-8 py-10">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700">
              Email Address
            </label>
            <input
              {...register('email')}
              type="email"
              id="email"
              className={`mt-1 appearance-none relative block w-full px-3 py-2 border rounded-md placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm ${
                errors.email ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="Enter your email address"
              autoComplete="email"
              autoFocus
            />
            <div className="h-5 mt-1">
              {errors.email && (
                <p className="text-sm text-red-600">{errors.email.message}</p>
              )}
            </div>
          </div>

          <button
            type="submit"
            disabled={isSubmitting || forgotPasswordMutation.isPending}
            className={`group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors ${
              isSubmitting || forgotPasswordMutation.isPending
                ? 'cursor-not-allowed'
                : 'hover:opacity-90'
            }`}
            style={{
              background: isSubmitting || forgotPasswordMutation.isPending
                ? '#fca5a5'
                : 'linear-gradient(90deg, #ff3160 0%, #cc1a40 50%, #ff3160 100%)',
            }}
          >
            {isSubmitting || forgotPasswordMutation.isPending ? (
              <>
                <LoadingSpinner size="md" variant="white" className="-ml-1 mr-3" label="Sending" />
                Sending...
              </>
            ) : (
              'Send reset link'
            )}
          </button>

          <div className="text-center pt-2">
            <Link
              to="/login"
              className="text-sm text-primary-600 hover:text-primary-700"
            >
              Back to sign in
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
